import { useState, useContext, useEffect } from "react";
import { SelectComponent } from "components/select/select";
import { createChannelOptions, createDateOptions, createGenreOptions, createPackageOptions, } from "core/lib/list-methods";
import { dataLayerPush, getAnalyticTags } from "core/lib/analytics-methods";
import context from "core/tv-guide.context";
import { observer } from "mobx-react-lite";
import { LayoutType, RootElementId } from "core/constants";
import { AppContext } from "core/providers/tv-guide.context";
import { GenreSelectOption, SelectOption } from "core/types";
import replaceSpaces from "core/lib/replace-spaces";
import PopupComponent from "./popup/popup";
import DownloadContent from "components/shared/download-content";

const dictionary = context.dictionary;

const TvGuideHeader = observer(() => {
  const appContext = useContext(AppContext);
  const store = appContext.store;

  // Select items
  const [channelList, setChannelList] = useState<SelectOption[]>([]);
  const [packageList, setPackageList] = useState<SelectOption[]>([]);
  const [genreList, setGenreList] = useState<GenreSelectOption[]>([]);
  const [genreValue, setGenreValue] = useState<string>("");
  const [dateList, setDateList] = useState<SelectOption[]>([]);
  const dateMode = false;

  const [singleChannel, setSingleChannel] = useState(false);

  const [popupState, setPopupState] = useState(false);
  const [popupInfo, setPopupInfo] = useState<number[]>([]);

  function popupCallback() {
    setPopupInfo([]);
    setPopupState(false);
  }

  useEffect(() => {
    if (!store.hasScheduleData || genreList.length) {
      return;
    }

    // Channels
    const channelOptions = createChannelOptions(store);

    setChannelList(channelOptions);

    if (!store.currentChannel) {
      store.updateCurrentChannel(parseInt(channelOptions[0]?.value));
    }

    // Package
    const packageOptions = createPackageOptions(store);
    setPackageList(packageOptions);

    // if (props.package === undefined) {
    //   props.setSelectCallback(pkList[0].value, 'package');
    // }

    // Genre
    const genres = createGenreOptions(store.genres);

    setGenreList(genres);

    // if (props.genre === undefined) {
    //   props.setSelectCallback(genres[0]?.value, 'genre');
    // }

    const currentRequestValue = store.searchCriteria;

    const equals = (a: string, b: string) => a?.toLowerCase() === b?.toLowerCase();
    const selectedItem = genres.find((genre: SelectOption) => (!currentRequestValue.subGenre && equals(genre.label, currentRequestValue.genre ?? "")) || equals(genre.label, currentRequestValue.subGenre ?? ""));
    // Programmatic genre selected value update through the context.

    if ((genreValue !== null
      && selectedItem
      && JSON.stringify(genreValue) !== JSON.stringify(selectedItem))
      || (genreValue === null && selectedItem && selectedItem?.value !== "")) {
      setGenreValue(selectedItem.value);
    }

    // Dates
    const dates = createDateOptions();
    setDateList(dates);

    //This should be moved out & it doesn't seem to work in prod
    // const dtNumber = parseInt(
    //   window.location.search.replace(/(date=)(\d*)/, '$2').replace(/\?/, ''),
    //   10
    // );
    // const searchParams = new URLSearchParams(window.location.search);
    // const dateParam = searchParams.get("date");

    // if (dateParam) {
    //   setDateMode(true);

    //   const foundDate = dates.find((dt: any) => {
    //     return dt.value >= dtNumber;
    //   });

    //   if (foundDate) {

    //     // props.setSelectCallback(foundDate.value, 'date');

    //     TVGuideContext.fetchNewData('date', foundDate.value);
    //   }
    //   return;
    // }

    // if (props.dateRange === undefined) {
    // props.setSelectCallback(dates[0].value, 'date');

    const rootEl = document.getElementById(RootElementId) as HTMLElement;

    //TODO: Move code elsewhere
    const shortChannels = rootEl.getAttribute("data-channel-shortlist");

    if (shortChannels && shortChannels.length > 0) {
      setSingleChannel(true);

      const faves = shortChannels.split("").map((item) => parseInt(item, 10));
      store.updateFavouriteChannelNumbers(faves);
    }
    // }
  }, [store, store.schedule, store.genres, store.hasScheduleData, genreValue]);

  const onQuickGenreLinkClickHandle = (item: string) => {
    const genre = store.genres.find(x => x.mainGenre === item);
    const genreValue = (genre?.value || genre?.mainGenre) ?? "";
    appContext.updateGenre(genreValue);
  }

  const selectLayout = (type: string) => {
    store.updateLayout(type);
    dataLayerPush("layout-switch-user", [type, new Date()]);
  }

  const LayoutButton = ({ type, icon }: { type: string, icon: string }) => {
    const isActive = store.layout === type;

    let css = `button tvg-header-button btn-${type}`;
    css += isActive ? " active" : "";

    return (<button type="button" className={css} onClick={() => selectLayout(type)}>
      <span className={icon}></span>
    </button>);
  }

  const FavouritesButton = () => {
    if (store.layout !== LayoutType.grid || context.channelTags) {
      return null;
    }

    let css = "button tvg-header-button btn-favourite";
    css += store.showFavourites ? " active" : "";

    return (
      <button
        type="button"
        className={css}
        onClick={() => {
          const newState = store.showFavourites ? false : store.hasFavourites;
          store.updateShowFavourites(newState);
        }}
      >
        <span className="icon-star_full star"></span>
      </button>
    );
  }

  const isList = store.layout === "list";

  if (singleChannel) {
    return (<div className="tv-guide-header container"></div>);
  }

  return (
    <div className="tv-guide-header container">
      <h1>{dictionary.tvGuide}</h1>
      <div className="drop-down-holder">
        {/* Package */}
        {
          !context.channelTags && (
            <SelectComponent
              selectType="package"
              list={packageList}
              callback={(item: SelectOption) => appContext.updatePackage(item.value)}
              value={store.searchCriteria.package}
              analyticTags={(value: string) => getAnalyticTags({
                component_name: "change_package_selected",
                component_type: "package_selected",
                package_name: replaceSpaces(value)
              })}
            />
          )
        }
        {/* Genre */}
        {
          !context.channelTags && (
            <SelectComponent
              selectType="genre"
              list={genreList}
              callback={(item: GenreSelectOption) => {
                appContext.updateGenre(item.value, item.subGenre);
              }}
              value={store.searchCriteria.genre}
              analyticTags={(value: string) => getAnalyticTags({
                component_name: "change_genre_selected",
                component_type: "genre_selected",
                genre_type: replaceSpaces(value)
              })}
            />
          )
        }
        {
          context.fifaWorldCup && isList && (
            <div className='fifa-world-cup-quick-link quick-link-list-item'>
              <p>{dictionary.fifaWorldCupLinkText}<span {...getAnalyticTags({
                component_name: "fifa_quick_link_genre_selected",
                component_type: "genre_selected",
                genre_type: "FIFA World Cup",
              })} onClick={() => onQuickGenreLinkClickHandle("FIFA World Cup")}>{` ${dictionary.clickHere}`}</span></p>
            </div>
          )
        }
        {/* Channel */}
        {
          isList && (
            <SelectComponent
              selectType="channel"
              list={channelList}
              value={store.currentChannel}
              icons={true}
              isChannel={true}
              callback={(item: SelectOption) => {
                store.updateCurrentChannel(parseInt(item.value));
              }}
              analyticTags={(value: string) => getAnalyticTags({
                component_name: "change_channel_selected",
                component_type: "channel_selected",
                channel_name: replaceSpaces(value)
              })}
            />
          )
        }
        {/* Dates */}
        {
          isList && !dateMode && (
            <SelectComponent
              selectType="date"
              list={dateList}
              value={store.dateOption}
              callback={(item: SelectOption) => {
                store.updateDateOption(item.value);
              }}
            />
          )
        }
      </div>
      <div className="button-holder">
        {!dateMode && !context.channelTags && (
          <button
            type="button"
            className="button tvg-header-button btn-download"
            onClick={() => {
              const channels = context.storage.favourites;

              if (store.layout === LayoutType.grid) {
                setPopupInfo(channels);
              }

              if (isList) {
                setPopupInfo([store.currentChannel]);
              }

              setPopupState(true);
            }}
          >
            <span className="icon-downloads"></span>
          </button>
        )}

        <FavouritesButton />

        <LayoutButton type={LayoutType.grid} icon="icon-grid-v" />

        <LayoutButton type={LayoutType.list} icon="icon-list" />
      </div>

      <PopupComponent showPopup={popupState} popupCallback={popupCallback}>
        <DownloadContent
          popupInfo={popupInfo}
        // channel={props.channel}
        />
      </PopupComponent>
    </div>
  );
});

export default TvGuideHeader;
